import { Component, Input, Output, EventEmitter, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import { 
    Invoice, 
    DocumentHelper, 
    DocumentLike,
    DirectionBadge, 
    SourceBadge, 
    PaymentStatusInfo,
    KsefStatusInfo
} from 'fakturnia-shared';
import { DocumentTypeNamePipe } from 'shared/pipes/document-type-name.pipe';
import { DateFormatterComponent } from 'shared/components/date-formatter/date-formatter.component';
import { DialogService } from 'shared/services/dialog.service';
import { KsefActionsService } from 'shared/services/ksef-actions.service';

/**
 * Komponent wiersza dokumentu w liście dokumentów.
 * 
 * Używa DocumentHelper z fakturnia-shared do wszystkich operacji na dokumencie:
 * - Badge'y (kierunek, źródło, status płatności, status KSeF)
 * - Uprawnienia (canEdit, canDelete, canSendToKsef, etc.)
 * - Gettery (getDocumentName, getBuyerName, getGrossAmount, etc.)
 * 
 * Dzięki temu logika jest współdzielona między fakturapp i Fakturnia.Mobile.
 */
@Component({
    selector: 'app-document-row',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltipModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        DocumentTypeNamePipe,
        DateFormatterComponent
    ],
    templateUrl: './document-row.component.html',
    styleUrls: ['./document-row.component.scss']
})
export class DocumentRowComponent implements OnInit, OnDestroy {
    
    private _dialogService = inject(DialogService);
    private _ksefActionsService = inject(KsefActionsService);
    private _destroy$ = new Subject<void>();
    
    @Input() document!: DocumentLike;
    @Input() isDraftsView = false;
    @Input() enableKsefFeatures = true;
    
    /** Czy ten dokument jest w trakcie wysyłki do KSeF */
    isSendingToKsef = false;

    /** Czy menu kontekstowe jest otwarte */
    isMenuOpen = false;
    
    ngOnInit(): void {
        this._ksefActionsService.sendState$
            .pipe(takeUntil(this._destroy$))
            .subscribe(state => {
                const docId = this.getDocumentId();
                this.isSendingToKsef = state.documentId === docId && state.status === 'sending';
            });
    }
    
    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
    
    @Output() searchByNip = new EventEmitter<string>();
    @Output() markAsPaid = new EventEmitter<string>();
    @Output() markAsUnpaid = new EventEmitter<string>();
    @Output() markAsIssued = new EventEmitter<string>();
    @Output() downloadXml = new EventEmitter<string>();
    @Output() downloadPdf = new EventEmitter<string>();
    @Output() downloadUpo = new EventEmitter<string>();
    @Output() sendToKsef = new EventEmitter<string>();
    @Output() issueAndSendToKsef = new EventEmitter<string>();
    @Output() createAdvanceInvoice = new EventEmitter<string>();
    
    // ========================================================================
    // DOCUMENT TYPE CHECKS (delegowane do DocumentHelper)
    // ========================================================================
    
    /**
     * Sprawdza czy dokument jest zamówieniem (Zamowienie, Oferta)
     */
    isOrderDocument(): boolean {
        return DocumentHelper.isOrderDocument(this.document);
    }
    
    // ========================================================================
    // PAYMENT STATUS (delegowane do DocumentHelper)
    // ========================================================================

    /**
     * Zwraca informacje o statusie płatności dla wyświetlenia w kolumnie
     */
    getPaymentStatusInfo(): PaymentStatusInfo {
        return DocumentHelper.getPaymentStatusInfo(this.document);
    }
    
    /**
     * Sprawdza czy termin płatności minął
     */
    isPaymentOverdue(): boolean {
        return DocumentHelper.isPaymentOverdue(this.document);
    }
    
    // ========================================================================
    // PAYMENT ACTIONS
    // ========================================================================
    
    /**
     * Czy można oznaczyć jako zapłacone
     */
    canMarkAsPaid(): boolean {
        return DocumentHelper.canMarkAsPaid(this.document);
    }
    
    /**
     * Czy można cofnąć zapłatę
     */
    canMarkAsUnpaid(): boolean {
        return DocumentHelper.canMarkAsUnpaid(this.document);
    }
    
    /**
     * Emituje event oznaczenia jako zapłacone
     */
    onMarkAsPaid(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.markAsPaid.emit(this.getDocumentId());
    }
    
    /**
     * Emituje event cofnięcia zapłaty
     */
    onMarkAsUnpaid(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.markAsUnpaid.emit(this.getDocumentId());
    }
    
    // ========================================================================
    // DRAFT ACTIONS
    // ========================================================================
    
    /**
     * Czy dokument jest szkicem (można oznaczyć jako wystawiony)
     */
    canMarkAsIssued(): boolean {
        return DocumentHelper.canMarkAsIssued(this.document);
    }
    
    /**
     * Emituje event oznaczenia jako wystawiony
     */
    onMarkAsIssued(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.markAsIssued.emit(this.getDocumentId());
    }
    
    // ========================================================================
    // DOWNLOAD ACTIONS
    // ========================================================================
    
    /**
     * Emituje event pobrania XML (dla faktur z KSeF)
     */
    onDownloadXml(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.downloadXml.emit(this.getDocumentId());
    }
    
    /**
     * Emituje event pobrania PDF (dla lokalnych faktur)
     */
    onDownloadPdf(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.downloadPdf.emit(this.getDocumentId());
    }
    
    /**
     * Emituje event pobrania UPO (dla faktur wysłanych do KSeF)
     */
    onDownloadUpo(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.downloadUpo.emit(this.getDocumentId());
    }
    
    /**
     * Czy można pobrać UPO (tylko gdy dokument ma ksefUpo)
     */
    canDownloadUpo(): boolean {
        return !!(this.document as any).ksefUpo;
    }
    
    // ========================================================================
    // KSEF ACTIONS
    // ========================================================================
    
    /**
     * Emituje event wysyłki do KSeF
     */
    onSendToKsef(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.sendToKsef.emit(this.getDocumentId());
    }
    /**
     * Emituje event wystawienia i wysłania do KSeF (dla szkiców)
     */
    onIssueAndSendToKsef(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.issueAndSendToKsef.emit(this.getDocumentId());
    }    
    // ========================================================================
    // ADVANCE INVOICE ACTIONS
    // ========================================================================
    
    /**
     * Emituje event tworzenia faktury zaliczkowej (tylko dla zamówień)
     */
    onCreateAdvanceInvoice(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.createAdvanceInvoice.emit(this.getDocumentId());
    }
    
    /**
     * Czy można wystawić fakturę zaliczkową (tylko dla zamówień typu Zamowienie)
     */
    canCreateAdvanceInvoice(): boolean {
        return DocumentHelper.canCreateAdvanceInvoice(this.document);
    }
    
    // ========================================================================
    // BADGES (delegowane do DocumentHelper)
    // ========================================================================
    
    /**
     * Zwraca badge kierunku transakcji
     */
    getDirectionBadge(): DirectionBadge {
        return DocumentHelper.getDirectionBadge(this.document);
    }
    
    /**
     * Zwraca badge źródła dokumentu
     */
    getSourceBadge(): SourceBadge {
        return DocumentHelper.getSourceBadge(this.document);
    }
    
    // ========================================================================
    // KSEF STATUS (delegowane do DocumentHelper)
    // ========================================================================
    
    /**
     * Sprawdza czy data wystawienia jest przeterminowana (przed dzisiejszą datą)
     */
    isIssueDateExpired(): boolean {
        return DocumentHelper.isIssueDateExpired(this.document);
    }
    
    /**
     * Zwraca pełne informacje o statusie KSeF
     */
    getKsefStatus(): KsefStatusInfo {
        return DocumentHelper.getKsefStatus(this.document);
    }
    
    /**
     * Zwraca ikonę statusu KSeF
     */
    getKsefStatusIcon(): string {
        return DocumentHelper.getKsefStatusIcon(this.document);
    }
    
    /**
     * Zwraca klasę koloru dla statusu KSeF
     */
    getKsefStatusColor(): string {
        return DocumentHelper.getKsefStatusColor(this.document);
    }
    
    /**
     * Zwraca tooltip dla statusu KSeF
     */
    getKsefStatusTooltip(): string {
        return DocumentHelper.getKsefStatusTooltip(this.document);
    }
    
    /**
     * Sprawdza czy dokument jest zsynchronizowany z KSeF
     */
    isKsefSynced(): boolean {
        return DocumentHelper.isKsefSynced(this.document);
    }
    
    /**
     * Sprawdza czy dokument wymaga KSeF
     */
    isKsefRequired(): boolean {
        return DocumentHelper.isKsefRequired(this.document);
    }
    
    // ========================================================================
    // BASIC GETTERS (delegowane do DocumentHelper)
    // ========================================================================
    
    /**
     * Zwraca nazwę dokumentu (invoiceName lub documentNumber)
     */
    getDocumentName(): string {
        // DEBUG: sprawdzanie problemu z nazwą dokumentu
        return DocumentHelper.getDocumentName(this.document);
    }
    
    /**
     * Zwraca nazwę kontrahenta (dla samofakturowania - sprzedawca, dla pozostałych - nabywca)
     */
    getContractorName(): string {
        return DocumentHelper.getContractorName(this.document);
    }
    
    /**
     * Zwraca NIP kontrahenta (dla samofakturowania - sprzedawca, dla pozostałych - nabywca)
     */
    getContractorNip(): string | null {
        return DocumentHelper.getContractorNip(this.document);
    }
    
    /**
     * Zwraca identyfikator kontrahenta z prefixem (NIP: lub VAT-UE:)
     */
    getContractorIdentifier(): { prefix: string; value: string } | null {
        return DocumentHelper.getContractorIdentifier(this.document);
    }
    
    /**
     * Kliknięcie w NIP/VAT-UE - emituje event do wyszukiwania
     */
    onNipClick(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        const identifier = this.getContractorIdentifier();
        if (identifier) {
            this.searchByNip.emit(identifier.value);
        }
    }
    
    /**
     * Zwraca kwotę brutto
     */
    getGrossAmount(): number {
        return DocumentHelper.getGrossAmount(this.document);
    }
    
    /**
     * Zwraca walutę
     */
    getCurrency(): string {
        return DocumentHelper.getCurrency(this.document);
    }
    
    /**
     * Zwraca datę wystawienia
     */
    getIssueDate(): string {
        return DocumentHelper.getIssueDate(this.document);
    }
    
    /**
     * Zwraca ID dokumentu
     */
    getDocumentId(): string {
        return DocumentHelper.getDocumentId(this.document);
    }
    
    // ========================================================================
    // PERMISSIONS (delegowane do DocumentHelper)
    // ========================================================================
    
    /**
     * Czy można edytować dokument
     */
    canEdit(): boolean {
        return DocumentHelper.canEdit(this.document);
    }
    
    /**
     * Czy można usunąć dokument
     */
    canDelete(): boolean {
        return DocumentHelper.canDelete(this.document);
    }
    
    /**
     * Czy można wysłać do KSeF
     */
    canSendToKsef(): boolean {
        return DocumentHelper.canSendToKsef(this.document);
    }
    
    /**
     * Czy można pobrać XML
     */
    canDownloadXml(): boolean {
        return DocumentHelper.canDownloadXml(this.document);
    }
    
    /**
     * Czy można pobrać PDF
     */
    canDownloadPdf(): boolean {
        return DocumentHelper.canDownloadPdf(this.document);
    }
    
    // ========================================================================
    // UTILITY METHODS
    // ========================================================================
    
    /**
     * Zatrzymuje propagację kliknięcia (dla menu)
     */
    stopPropagation(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }
    
    // ========================================================================
    // DELETE ACTION
    // ========================================================================
    
    /**
     * Obsługuje usuwanie dokumentu z dialogiem potwierdzenia
     */
    onDelete(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this._dialogService.confirmDeleteInvoiceDialog(this.document);
    }
}
